import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wrench, Calendar, Type, Palette, Flower2, MessageSquare, FileText } from "lucide-react";
import { OrderDetailData, WorkDetails } from "@/types/orders";

interface OrderWorkDetailsCardProps {
  order: OrderDetailData;
}

export function OrderWorkDetailsCard({ order }: OrderWorkDetailsCardProps) {
  // Etsi ensimmäinen tilausrivi, jolla on work_details
  const lineWithWorkDetails = order.order_lines?.find(
    (line) => line.work_details && Object.values(line.work_details as object).some((v) => v)
  );

  const workDetails = lineWithWorkDetails?.work_details as WorkDetails | undefined;

  if (!workDetails) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Työn tiedot
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Ei työn tietoja</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          Työn tiedot
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {workDetails.name && (
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Nimi</p>
            <p className="font-medium text-lg">{workDetails.name}</p>
          </div>
        )}

        {(workDetails.birth_date || workDetails.death_date) && (
          <div className="flex items-start gap-3">
            <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Päivämäärät</p>
              <p className="font-medium">
                {workDetails.birth_date || "?"} – {workDetails.death_date || "?"}
              </p>
            </div>
          </div>
        )}

        {workDetails.font && (
          <div className="flex items-start gap-3">
            <Type className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Fontti</p>
              <p className="font-medium">{workDetails.font}</p>
            </div>
          </div>
        )}

        {workDetails.engraving_style && (
          <div className="flex items-start gap-3">
            <Palette className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Kaiverrustyyli</p>
              <p className="font-medium">{workDetails.engraving_style}</p>
            </div>
          </div>
        )}

        {workDetails.text_size && (
          <div className="flex items-start gap-3">
            <Type className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Tekstin koko</p>
              <p className="font-medium">{workDetails.text_size}</p>
            </div>
          </div>
        )}

        {workDetails.ornament && workDetails.ornament !== "Ei koristetta" && (
          <div className="flex items-start gap-3">
            <Flower2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Koriste</p>
              <p className="font-medium">{workDetails.ornament}</p>
            </div>
          </div>
        )}

        {workDetails.additional_text && (
          <div className="flex items-start gap-3">
            <MessageSquare className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Lisäteksti</p>
              <p className="font-medium italic">"{workDetails.additional_text}"</p>
            </div>
          </div>
        )}

        {workDetails.stone_description && (
          <div className="flex items-start gap-3">
            <FileText className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Kiven kuvaus</p>
              <p className="font-medium">{workDetails.stone_description}</p>
            </div>
          </div>
        )}

        {workDetails.notes && (
          <div className="pt-2 border-t">
            <p className="text-sm text-muted-foreground">Huomiot</p>
            <p className="text-sm whitespace-pre-wrap">{workDetails.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
